import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Card, CardContent } from '@/components/ui/card'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'

interface GenericContentPageProps {
  title: string
  contentPath?: string
  lastUpdated?: string
}

export function GenericContentPage({ title, contentPath, lastUpdated = '2024-03-30' }: GenericContentPageProps) {
  const [content, setContent] = useState('')
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([])

  useEffect(() => {
    if (contentPath) {
      fetch(contentPath)
        .then(res => res.text())
        .then(text => {
          // Remove frontmatter if present
          const cleanContent = text.replace(/^---[\s\S]*?---/, '').trim()
          setContent(cleanContent)

          // Extract headings for sidebar
          const headingMatches = cleanContent.match(/^#\s+(.+)$/gm)
          if (headingMatches) {
            const extracted = headingMatches.map(h => {
              const text = h.replace(/^#\s+/, '')
              const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
              return { id, text }
            })
            setHeadings(extracted)
          }
        })
    }
  }, [contentPath])

  return (
    <div className="container mx-auto max-w-7xl px-4 py-24">
      {/* Page Header */}
      <div className="mb-12 text-center md:text-left">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">{title}</h1>
        <div className="flex items-center justify-center md:justify-start gap-4 text-muted-foreground">
          <Badge variant="outline" className="text-xs">Legal Document</Badge>
          <span className="text-sm">Last updated: {lastUpdated}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-12 items-start">
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block sticky top-24 space-y-4">
          <h4 className="font-bold text-sm uppercase tracking-wider text-primary">In this document</h4>
          <nav className="flex flex-col gap-2">
            {headings.map(h => (
              <a 
                key={h.id} 
                href={`#${h.id}`} 
                className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all"
              >
                {h.text}
              </a>
            ))}
          </nav>
        </aside>

        {/* Main Content Card */}
        <div className="max-w-3xl">
          <Card className="glass-panel border-white/10 shadow-2xl overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="prose dark:prose-invert prose-headings:scroll-mt-24 prose-headings:font-bold prose-h1:text-3xl prose-h1:mb-8 prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-muted-foreground prose-p:leading-relaxed prose-table:border prose-table:rounded-lg prose-th:bg-muted/50 prose-th:p-3 prose-td:p-3">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => {
                      const text = String(children)
                      const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
                      return <h1 id={id}>{children}</h1>
                    }
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
